import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { ShieldCheck, ShieldAlert, ShieldX } from 'lucide-react';
import { BowtieNode } from '../bowtie.types';

export const BarrierNode: React.FC<{ data: BowtieNode & { onSelect?: (node: BowtieNode) => void } }> = memo(
  ({ data }) => {
    const isPrevention = data.type === 'preventionBarrier';
    const status = data.metadata?.status || 'ok';
    const score = data.metadata?.effectivenessScore ?? 70;

    const statusConfig = {
      ok: {
        bg: '#ecfdf5',
        border: '#10b981',
        text: '#047857',
        label: 'EFFECTIVE',
        icon: <ShieldCheck size={14} color="#10b981" />,
        barColor: '#10b981',
      },
      weak: {
        bg: '#fffbeb',
        border: '#f59e0b',
        text: '#b45309',
        label: 'DEGRADED / WEAK',
        icon: <ShieldAlert size={14} color="#f59e0b" />,
        barColor: '#f59e0b',
      },
      failed: {
        bg: '#fef2f2',
        border: '#ef4444',
        text: '#b91c1c',
        label: 'FAILED / COMPROMISED',
        icon: <ShieldX size={14} color="#ef4444" />,
        barColor: '#ef4444',
      },
    }[status];

    return (
      <div
        onClick={() => data.onSelect && data.onSelect(data)}
        style={{
          background: statusConfig.bg,
          border: `2px solid ${statusConfig.border}`,
          borderRadius: '12px',
          padding: '12px 14px',
          width: '260px',
          minHeight: '108px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.15s ease',
        }}
        title="Click to view details or edit barrier effectiveness"
      >
        <Handle id="left" type="target" position={Position.Left} style={{ background: statusConfig.border, width: 8, height: 8 }} />

        {/* Header: Role & Status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <span
            style={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              color: isPrevention ? '#0284c7' : '#7c3aed',
            }}
          >
            {isPrevention ? 'PREVENTION' : 'MITIGATION'}
          </span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '9px',
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: '8px',
              background: '#ffffff',
              border: `1px solid ${statusConfig.border}`,
              color: statusConfig.text,
            }}
          >
            {statusConfig.icon}
            {statusConfig.label}
          </span>
        </div>

        {/* Title */}
        <div style={{ fontWeight: 600, fontSize: '13px', color: '#1e293b', lineHeight: 1.3 }}>
          {data.label}
        </div>

        {/* Effectiveness score bar */}
        <div style={{ marginTop: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#64748b', marginBottom: '3px' }}>
            <span>Effectiveness</span>
            <span style={{ fontWeight: 700, color: statusConfig.text }}>{score}%</span>
          </div>
          <div style={{ background: '#e2e8f0', borderRadius: '4px', height: '5px', overflow: 'hidden' }}>
            <div style={{ background: statusConfig.barColor, width: `${Math.min(score, 100)}%`, height: '100%' }} />
          </div>
        </div>

        {data.metadata?.owner && (
          <div style={{ fontSize: '10px', color: '#64748b', marginTop: '6px' }}>
            Dept: <strong>{data.metadata.owner}</strong>
          </div>
        )}

        <Handle id="right" type="source" position={Position.Right} style={{ background: statusConfig.border, width: 8, height: 8 }} />
      </div>
    );
  }
);
